import type { FC } from 'react';
import { Button, Modal } from '@selfinspectionorg/ui-kit';
import { PasswordInput } from '@/components/form/PasswordInput';

interface Props {
  isOpen: boolean;
  /** Modal heading, e.g. "Confirm it's you". */
  title: string;
  /** Sub-heading naming the factor being set up. */
  description: string;
  password: string;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  isVerifying: boolean;
  /** Inline error from a rejected password; keeps the prompt open when set. */
  error: string | null;
}

/**
 * Shared step-up prompt: collects the current password and hands it to the caller's start call. Used
 * verbatim by the TOTP, email, and passkey add flows so the copy and wrong-password handling stay
 * consistent. Presentational — the password lives in the caller's `useStepUp` hook, not here.
 */
export const StepUpModal: FC<Props> = ({
  isOpen,
  title,
  description,
  password,
  onPasswordChange,
  onSubmit,
  onClose,
  isVerifying,
  error,
}) => (
  <Modal.Wrapper isOpen={isOpen} onClose={onClose} id="mfaStepUpModal" width="400px">
    <Modal.Title titleText={title} additionalText={description} />
    <PasswordInput
      name="currentPassword"
      label="Current password"
      value={password}
      onChange={(event) => onPasswordChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && password && !isVerifying) {
          onSubmit();
        }
      }}
      errored={!!error}
      helperMessage={error || undefined}
      autoFocus
      testId="mfa-stepup-password-input"
    />
    <Modal.ActionButtons onCancelButtonClick={onClose}>
      <Button
        variant="primary"
        width="132px"
        onClick={onSubmit}
        isLoading={isVerifying}
        disabled={!password}
        data-testid="mfa-stepup-submit-button"
      >
        Continue
      </Button>
    </Modal.ActionButtons>
  </Modal.Wrapper>
);
